<template>
  <div :style="cssVariablesBinding">
    <Theme4BaseTheHeader />

    <div v-if="shouldShowComponent" id="msgboxxWidget" />

    <main class="n-main-container">


      <slot />
    </main>

    <Theme4BaseTheFooter />
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import IndexGlobal from '~/mixins/index.global';
import LayoutsMixin from '~/mixins/layouts.mixin';
import Theme4BaseFooterOne from '~/components/theme4/base/footer/FooterOne.vue';
import { useWebsiteStore } from '~/units/website/store';
import AvailableComponentsForTheme4 from '~/components/theme4/AvailableComponentsForTheme4.vue';

export default defineNuxtComponent({
  name: 'Theme4',

  mixins: [IndexGlobal, LayoutsMixin],

  components: { AvailableComponentsForTheme4, Theme4BaseFooterOne },

  data() {
    return {
      containers: [
        {
          name: 'BLOGS_VIEW',
          classes: 'blog-wrap5',
        },
        {
          name: 'BLOG_DETAIL_VIEW',
          classes: 'blog-wrap6',
        },
      ],
    };
  },

  head(context) {
    const appMode = useAppMode();

    if (appMode.server && context.ssrContext) {
      const websiteStore = useWebsiteStore();
      const isBeercocks = websiteStore.company?.company_key === 'beercocks';

      const tagScripts = (() => {
        if (process.env.APP_STAGE === 'production') {
          return [
            ...websiteStore.googleTagsScripts,
            {
              key: 'company-analytics',
              type: 'text/javascript',
              src: `/theme4/js/${websiteStore.company?.company_key}/index.js`,
              async: false,
            },
          ];
        }
        return [];
      })();

      const scripts = [
        ...tagScripts,
        ...(isBeercocks
          ? [
              {
                src: 'https://cdn.msgboxx.io/static/js/bundle.min.js',
                async: true,
                onload: "window.msgboxxClient='BEERCOCKS';",
                body: true,
              },
            ]
          : []),
      ];

      const path = context._route.path;

      if (isBeercocks && path === '/our-offices/willerby') {
        scripts.push({
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org ',
            '@type': 'LocalBusiness',
            name: 'Beercocks Willerby',
            image: 'https://files.beercocks.neuronwebsites.co.uk/beercocks-logo-wgnsupi8t.webp',
            '@id': '',
            url: 'https://beercocks.com/our-offices/willerby',
            telephone: '01482 656789',
            priceRange: '£50,000 - £2,000,000',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '8 Kingston Road Hull',
              addressLocality: 'Willerby',
              postalCode: 'HU10 6BN',
              addressCountry: 'GB',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: 53.7570181,
              longitude: -0.4459721,
            },
            openingHoursSpecification: [
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                opens: '09:00',
                closes: '17:30',
              },
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: 'Saturday',
                opens: '09:00',
                closes: '13:00',
              },
            ],
          }),
          hid: 'local-business-schema',
          tagPosition: 'head',
        });
      }

      if (isBeercocks && path === '/our-offices/hedon') {
        scripts.push({
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            name: 'Beercocks Hedon',
            image: 'https://files.beercocks.neuronwebsites.co.uk/beercocks-logo-wgnsupi8t.webp',
            '@id': 'https://beercocks.com/our-offices/hedon',
            url: 'https://beercocks.com/our-offices/hedon',
            telephone: '01482 891234',
            priceRange: '£50,000 - £2,000,000',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '17 Market Place',
              addressLocality: 'Hedon',
              postalCode: 'HU12 8JA',
              addressCountry: 'GB',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: 53.7413766,
              longitude: -0.1990121,
            },
            openingHoursSpecification: [
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                opens: '09:00',
                closes: '17:30',
              },
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: 'Saturday',
                opens: '09:00',
                closes: '13:00',
              },
            ],
          }),
          hid: 'local-business-schema',
          tagPosition: 'head',
        });
      }

      if (isBeercocks && path === '/our-offices/beverley') {
        scripts.push({
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org ',
            '@type': 'LocalBusiness',
            name: 'Beercocks Beverley',
            image: 'https://files.beercocks.neuronwebsites.co.uk/beercocks-logo-wgnsupi8t.webp',
            '@id': '',
            url: 'https://beercocks.com/our-offices/beverley ',
            telephone: '01482 343399',
            priceRange: '£50,000 - £2,000,000',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '1 Saturday Market',
              addressLocality: 'Beverley',
              postalCode: 'HU17 8BB',
              addressCountry: 'GB',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: 53.8434003,
              longitude: -0.4331901,
            },
            openingHoursSpecification: [
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                opens: '09:00',
                closes: '17:30',
              },
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: 'Saturday',
                opens: '09:00',
                closes: '13:00',
              },
            ],
          }),
          hid: 'local-business-schema',
          tagPosition: 'head',
        });
      }

      if (isBeercocks && path === '/') {
        // Organization schema
        scripts.push({
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org ',
            '@type': 'Organization',
            name: 'Beercocks Estate Agents',
            url: 'https://beercocks.com/ ',
            logo: 'https://files.beercocks.neuronwebsites.co.uk/beercocks-logo-wgnsupi8t.webp',
            sameAs: [
              'https://www.facebook.com/beercocks ',
              'https://www.instagram.com/beercocksltd/ ',
              'https://www.youtube.com/@beercocksestateagent ',
            ],
          }),
          hid: 'organization-schema',
          tagPosition: 'head',
        });

        scripts.push({
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org ',
            '@type': 'LocalBusiness',
            name: 'Beercocks Estate Agents',
            image: 'https://files.beercocks.neuronwebsites.co.uk/beercocks-logo-wgnsupi8t.webp',
            '@id': '',
            url: 'https://beercocks.com/ ',
            telephone: '01482 343399',
            priceRange: '£50,000 - £2,000,000',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '154 Newland Avenue',
              addressLocality: 'Hull',
              postalCode: 'HU5 2NN',
              addressCountry: 'GB',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: 53.7645402,
              longitude: -0.3584025,
            },
            openingHoursSpecification: [
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                opens: '09:00',
                closes: '17:30',
              },
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: 'Saturday',
                opens: '09:00',
                closes: '13:00',
              },
            ],
          }),
          hid: 'local-business-schema',
          tagPosition: 'head',
        });
      }

      const links = [
        {
          href: `/theme4/css/${websiteStore.company?.company_key}/index.css`,
          rel: 'stylesheet',
          hid: `css/${websiteStore.company?.company_key}/index.css`,
          type: 'text/css',
        },
        ...(isBeercocks
          ? [
              {
                href: `https://cdn.msgboxx.io/static/css/bundle.min.css`,
                rel: 'stylesheet',
                type: 'text/css',
              },
            ]
          : []),
      ];

      const meta = [];
      if (isBeercocks) {
        meta.push({
          name: 'google-site-verification',
          content: 'a92UwyVgSuaCIpTfeqBMziIp49w0a5gXBp9tGrYzJYY',
        });
      }

      return {
        link: links,
        script: scripts,
        meta,
      };
    }
  },

  computed: {
    ...mapState(useWebsiteStore, ['currentPage']),

    isBeercocks() {
      return useWebsiteStore().company?.company_key === 'beercocks';
    },

    shouldShowComponent() {
      const currentPath = this.$route.path;
      const isInHeadsUpAlerts = currentPath.startsWith('/heads-up-alerts');
      return this.isBeercocks && !isInHeadsUpAlerts;
    },

    cssVariablesBinding() {
      return {
        ...this.cssVariables,
        '--c-primary': this.colors.primary,
        '--c-secondary': this.colors.secondary,
        '--c-border': this.colors.border,
        '--c-input_placeholder': this.colors.input_placeholder,
        '--c-text_white': this.colors.text_white,
        '--c-text_black': this.colors.text_black,
        '--f-primary': this.colors.primary_font,
        '--f-secondary': this.colors.secondary_font,
        '--box-checked-color': this.colors.secondary,
        '--checkbox-checked-color': this.colors.secondary,
        '--white': this.colors.text_white,
        '--default-font': this.colors.primary_font,
        '--default-title-font': this.colors.primary_font,
        '--checkbox-tick-color': 'white',
        '--input--placeholder': this.colors.input_placeholder,
        '--nc-secondary-bg-color': this.colors.primary,
        '--seashell': this.colors.secondary,
        '--dark-brown': '#666666',
        '--default-border-color': this.colors.border,
        '--nc-popup-background-color': 'white',
        '--nc-property-status-bg-color': this.colors.secondary,
        '--nc-property-status-text-color': 'white',
        '--nc-doughnut-chart-value': this.colors.primary,
        '--akya-text-color': this.colors['akya-text-color'],
        '--akya-background-color': this.colors['akya-background-color'] || this.colors['--akya-background-color'],
        '--akya-confirm-button-text-color': this.colors['akya-text-color'],
        '--akya-confirm-button-background-color': this.colors['akya-confirm-button-background-color'],
      };
    },
  },
});
</script>

<style scoped></style>
